import React from "react";
import "../DashboardStyles.css";
import ObjectiveTable from "./ObjectiveTable";

// ─── Data ────────────────────────────────────────────────────────────────────

// ObjectiveTable groups rows by `objective`; only the first row carries the label.
const withLabel = (label, rows) =>
  rows.map((r, i) => ({ ...r, objective: i === 0 ? label : "" }));

const goal1 = {
  number: "Goal 1",
  section: "9.1",
  title: "Strengthen the Foundations People and Businesses Need to Stay",
  intro: [
    <>
      Housing supply, childcare capacity, and wage levels function as a single
      system in the Panhandle. A worker who cannot find housing cannot take the
      job; a parent without childcare cannot keep it. Both constraints are lived
      rather than theoretical. Affordable housing draws a serious or moderate
      problem rating from 82% of survey respondents (tied for the highest-ranked
      issue in the survey) and 54% report that the lack of housing has affected
      them directly or affected someone they know. Childcare follows the same
      pattern. 53% of respondents report that childcare has kept them or someone
      they know out of the workforce (Chapter 11.3). This goal treats the two
      together because the evidence shows they operate together.
    </>,
  ],
  objectives: [
    {
      id: "1.1",
      label: "Objective 1.1 — Housing",
      target: (
        <>
          <strong>Beginning in 2027,</strong> support the completion of at least{" "}
          <strong>25 workforce housing units annually</strong> through
          PADD-administered or PADD-assisted programs, with priority in
          communities outside Scotts Bluff County. Regional housing production is
          tracked alongside this measure as context (U.S. Census Building Permits
          Survey, eleven-county total).
        </>
      ),
      rows: withLabel("1.1 Housing", [
        {
          strategy: "End of Year 1: Create a regional housing asset inventory and develop tools to facilitate future housing development.",
          lead: "PADD (lead); 6R1N, Nebraska DED, local economic development organizations",
          resources: "Staff time, DED CDBG planning funds, EDA planning grants",
          timeline: "Year 1",
        },
        {
          strategy: "By Year 2: Host regional meetings to implement the tools that have been developed to attract new development.",
          lead: "PADD, 6R1N, municipalities, Nebraska Home Builders Assn., DED, USDA RD",
          resources: "Staff time, EDA planning grants, community foundation support",
          timeline: "Year 2",
        },
        {
          strategy: "Years 2–5: Administer CDBG and USDA housing grants targeting rehabilitation and new construction. Track units completed, not grants submitted. Target: 25 units after baseline. Annual target: 25 units per year.",
          lead: "PADD, 6R1N, DED, USDA RD, municipalities, NIFA",
          resources: "CDBG Housing, USDA RD Section 502/504, HOME, NIFA",
          timeline: "Years 2–5",
        },
      ]),
      measures: [
        "# workforce housing units completed through PADD-administered or PADD-assisted programs, with the share outside Scotts Bluff County reported (target: 25 per year beginning 2027)",
        "# residential units authorized by building permit, eleven-county total (U.S. Census Building Permits Survey, annual)",
        "$ housing-related grant dollars awarded to Panhandle applicants",
        "# municipalities with a housing study or housing plan adopted within the prior five years",
        "# municipalities outside Scotts Bluff County with a housing project funded, permitted, or under construction",
      ],
    },
    {
      id: "1.2",
      label: "Objective 1.2 — Childcare",
      target: (
        <>
          <strong>By 2030,</strong> reduce the number of Panhandle communities
          with zero licensed childcare options by at least half from the Year 1
          baseline established under this objective.
        </>
      ),
      rows: withLabel("1.2 Childcare", [
        {
          strategy: "By Year 1: Map current licensed childcare capacity by county against estimated workforce demand. Establish a community-level baseline of licensed slots versus working-age households with children under 12.",
          lead: "PADD (lead); PPHD, Nebraska DHHS, Nebraska Children and Families Foundation",
          resources: "Staff time, PPHD data, DHHS Childcare Licensing data",
          timeline: "Year 1",
        },
        {
          strategy: "By Year 2: Deploy the incentives and programming developed in year 1 to support new in-home childcare providers, in partnership with the Panhandle Partnership’s childcare expansion initiative and its goal of 250 new licensed slots over 30 months.",
          lead: "PADD, 6R1N, local employers, CAPWN, Panhandle Partnership",
          resources: "Staff time, employer partnerships, DHHS, federal CCDBG",
          timeline: "Year 2",
        },
        {
          strategy: "Years 2–5: Support at least 20 new licensed childcare operations through business planning, facility planning, and grant navigation (measured by licensure, not application).",
          lead: "PADD, CAPWN, NBDC, local nonprofits, WNCC, DHHS",
          resources: "CDBG, USDA RD Community Facilities, DHHS, CCDBG, philanthropy",
          timeline: "Years 2–5",
        },
      ]),
      measures: [
        "# licensed childcare slots region-wide, all license types (Nebraska DHHS Childcare Licensing, annual)",
        "# municipalities with no licensed childcare provider within municipal limits (target: reduce from baseline)",
        "# employer-supported childcare partnerships formed, cumulative",
        "# new childcare operations reaching licensure with PADD support — measured by licensure, not application",
      ],
      notes: [
        {
          source: true,
          text: "Source note. The baseline is established in Year 1 from Nebraska Department of Health and Human Services childcare licensing records, counted at the community level by provider address. Licensed capacity is reported alongside provider counts, because a community with one small provider and a community with adequate capacity are not equivalent.",
        },
      ],
    },
  ],
};

const goal2 = {
  number: "Goal 2",
  section: "9.2",
  title: "Grow and Keep a Skilled Regional Workforce",
  intro: [
    <>Four conditions define this goal’s work, and each is documented in this cycle’s own evidence.</>,
    <>
      <strong>Wages lag.</strong> The Panhandle’s population-weighted per capita
      personal income is the lowest among Nebraska’s seven planning regions, and
      74% of survey respondents identify finding a good job with decent pay as a
      serious or moderate problem.
    </>,
    <>
      <strong>Young people leave.</strong> Out-migration draws the highest
      “serious problem” rating of any issue tested in the survey (58%).
      Sixty-two percent of residents disagree that young people have real
      opportunities to build a future here, and 41% of the region’s teens say
      they are unlikely to stay after high school.
    </>,
    <>
      <strong>Succession is unplanned.</strong> Forty-five percent of
      respondents personally know a Panhandle business owner weighing
      retirement or closure within ten years with no clear plan for the
      business, and 77% rate succession support as important or very important
      to the region’s economic future.
    </>,
    <>
      <strong>Skills gaps are employer-side.</strong> The demand-supply gaps in
      Chapter 5.7 come from state labor-market data rather than resident
      reports. Residents rank training access among their lower concerns, which
      is itself a finding: the constraint is visible to employers before it is
      visible to workers.
    </>,
  ],
  objectives: [
    {
      id: "2.1",
      label: "Objective 2.1 — Business Succession",
      target: (
        <>
          <strong>By 2030,</strong> facilitate documented ownership transitions
          for at least <strong>15 Panhandle businesses,</strong> retaining those
          jobs in the regional economy.
        </>
      ),
      rows: withLabel("2.1 Business Succession", [
        {
          strategy: "By Year 1: Partner with NBDC and REAP to establish a Panhandle Business Succession Registry, a voluntary list of owners within 10 years of retirement open to transition conversations. Target: 25 businesses enrolled by end of Year 1.",
          lead: "PADD (lead); NBDC, REAP, TCD, local chambers of commerce",
          resources: "Staff time, NBDC resources, REAP technical assistance, SBA",
          timeline: "Year 1",
        },
        {
          strategy: "By Year 2 and ongoing: Host at least 2 succession-planning forums annually — one eastern, one western — connecting retiring owners with potential buyers including young professionals, returning graduates, and existing employees.",
          lead: "PADD, NBDC, REAP, TCD, NEXT Young Professionals, CSC, WNCC, chambers",
          resources: "Staff time, venue sponsorships, NBDC, community foundations",
          timeline: "Years 2–5",
        },
        {
          strategy: "Years 1–5 (ongoing): Connect every enrolled business to a transition specialist within 90 days of enrollment. Track time-to-connection as a service standard.",
          lead: "PADD, NBDC, REAP, local accounting firms, Nebraska Bar Assn.",
          resources: "Staff time, NBDC, REAP, SBA, local professional service firms",
          timeline: "Years 1–5",
        },
      ]),
      measures: [
        "# businesses enrolled in the Succession Registry, cumulative (target: 25 by end of Year 1)",
        "# documented ownership transitions completed, cumulative (target: 15 total by 2030)",
        "# jobs retained through completed ownership transitions, measured in full-time equivalents at the transition date",
        "% of enrolled businesses connected to a transition specialist within 90 days of enrollment (target: 90%)",
      ],
    },
    {
      id: "2.2",
      label: "Objective 2.2 — Workforce Pipeline",
      target: (
        <>
          <strong>By 2030,</strong> increase the number of registered
          apprenticeships and work-based learning agreements between Panhandle
          employers and educational institutions by at least 50% over the Year 1
          baseline established under this objective.
        </>
      ),
      rows: withLabel("2.2 Workforce Pipeline", [
        {
          strategy: "Year 1: Conduct a regional employer needs survey (minimum 40 respondents) to identify the top 3 skills gaps by sector. Publish results to inform program development at WNCC, Chadron State, and local high schools.",
          lead: "PADD (lead); NDOL, WNCC, CSC, local chambers, DED",
          resources: "Staff time, NDOL Labor Market Information, DED outreach",
          timeline: "Year 1",
        },
        {
          strategy: "Years 1–3: Partner with WNCC and Panhandle high schools to formalize new employer-linked apprenticeship or internship pathways in high-demand sectors: ag technology, healthcare, skilled trades, and broadband infrastructure.",
          lead: "PADD, WNCC, CSC, Panhandle school districts, NDE, NDOL, employers",
          resources: "WIOA funds, NDOL training grants, Perkins Act, employer partnerships, DED",
          timeline: "Years 1–3",
        },
        {
          strategy: "Years 2–5: Track participant outcomes — did they stay in the Panhandle? Document employment status at 1 year and 3 years post-completion. Report findings annually to the CEDS Strategy Council.",
          lead: "PADD, WNCC, NDOL, participating employers, CSC",
          resources: "Staff time, NDOL data, WNCC/CSC records",
          timeline: "Years 2–5",
        },
      ]),
      measures: [
        "# active apprenticeships and work-based learning agreements with Panhandle employers (baseline Year 1, tracked annually)",
        "# unique employers participating in pipeline programs annually",
        "% of program participants employed in the Panhandle one year after completion, reported with the cohort response rate",
      ],
      notes: [
        {
          text: "Panhandle postsecondary persistence and employment-location trends are checked annually against NSWERS’s published dashboards (insights.nswers.org) as a state-maintained reference point. This is context, not a performance measure: it is a directional, trend-level indicator influenced by many regional and national factors, and it is not a measure of this CEDS’s direct effect.",
        },
      ],
    },
  ],
};

const goal3 = {
  number: "Goal 3",
  section: "9.3",
  title: "Build and Modernize the Infrastructure Communities Depend On",
  intro: [
    <>
      This is the one goal where the physical evidence runs ahead of resident
      experience, and the plan states that plainly. Much of the region’s water
      and wastewater predates 1960, broadband coverage is uneven across the
      eleven counties, and several transportation corridors carry loads they
      were not designed for. Residents, however, place water and sewer quality
      (35% serious or moderate) and internet access (19%) at the bottom of the
      eight community concerns tested. Half report no problem at all.
    </>,
    <>
      That gap itself is a finding. Infrastructure determines where growth can
      physically occur, whether or not current residents feel the constraint: a
      system at capacity forecloses the housing development called for in Goal
      1, and the households and employers a community hopes to attract are by
      definition not represented in a survey of people who already live here.
      Goal 3 rests on asset condition and future capacity rather than present
      complaint, and it is built that way deliberately. Infrastructure grant
      administration is PADD’s strongest and most established capability. What
      this cycle adds is sequencing, a tiered, publicly visible project pipeline
      so communities can see where their project stands and what moves it
      forward.
    </>,
  ],
  objectives: [
    {
      id: "3.1",
      label: "Objective 3.1 — Water and Wastewater",
      target: (
        <>
          <strong>By 2030,</strong> support the funding and initiation of
          construction for at least <strong>10</strong> water or wastewater
          infrastructure projects across the region, prioritized using PADD’s
          project scoring rubric.
        </>
      ),
      rows: withLabel("3.1 Water and Wastewater", [
        {
          strategy: "Year 1: Complete an infrastructure needs inventory across all eleven counties using PADD’s project scoring rubric to rank and tier projects. Publish a tiered project pipeline updated at each annual CEDS review.",
          lead: "PADD (lead); NDEE, USDA RD, NRDs, counties and municipalities",
          resources: "Staff time, EDA planning grants, USDA RD technical assistance",
          timeline: "Year 1",
        },
        {
          strategy: "Years 1–5: Submit a minimum of 2 infrastructure grant applications annually to EDA, USDA, CDBG, and state programs. Track application-to-award ratio as a performance measure.",
          lead: "PADD, municipalities, rural water districts, NRDs, USDA RD, NDEE, DED",
          resources: "EDA Public Works, USDA RD Water & Waste Disposal, CDBG Infrastructure, SRF",
          timeline: "Years 1–5",
        },
        {
          strategy: "Ongoing, Years 1–5: Maintain a public-facing project status tracker (Red/Yellow/Green) updated at each annual CEDS review. Red = unfunded and urgent; Yellow = application submitted; Green = funded or under construction.",
          lead: "PADD, municipalities, USDA RD, NDEE",
          resources: "Staff time; PADD website",
          timeline: "Years 1–5",
        },
      ]),
      measures: [
        "# infrastructure grant applications submitted and awarded annually (PADD-prepared and PADD-assisted), reported with the resulting award rate (no target in Year 1; target range set at the Year 1 review)",
        "$ total project investment leveraged through awarded applications, including local match",
        "# water or wastewater projects with construction initiated or complete, cumulative",
      ],
      notes: [
        { text: "The Red/Yellow/Green project tiering established in Year 1 is reported as a pipeline table in each annual review rather than as separate performance measures." },
        { text: "The award-rate target range will be set at the Year 1 review, once PADD’s own submission and award history has been compiled. The range will carry a ceiling as well as a floor: a rate below the range indicates applications are not competitive or are poorly matched to program criteria, while a rate above it indicates PADD is submitting only low-risk applications rather than pursuing the full project pipeline." },
      ],
    },
    {
      id: "3.2",
      label: "Objective 3.2 — Broadband",
      target: (
        <>
          <strong>By 2030,</strong> reduce the number of broadband serviceable
          locations in the Panhandle without access to 100/20 Mbps service by at
          least 50% from the Year 1 baseline established under this objective.
        </>
      ),
      rows: withLabel("3.2 Broadband", [
        {
          strategy: "Year 1: Complete a broadband gap assessment using FCC map data combined with community speed-test results. Identify and map top-priority unserved and underserved communities. Establish a verified baseline distinct from FCC’s reported coverage.",
          lead: "PADD (lead); Nebraska Broadband Office, ISPs, municipalities, NROC",
          resources: "EDA planning funds, NROC, nebraskaspeedtest.org data, staff time",
          timeline: "Year 1",
        },
        {
          strategy: "Years 1–3: Monitor NBO’s BEAD gap-fill rebidding process and advocate for Panhandle location coverage. Support at least 2 communities in pursuing supplemental broadband funding through USDA ReConnect, FCC E-Rate, state broadband grant programs, and forthcoming NTIA guidance on unallocated BEAD funds. Count locations connected, not applications submitted.",
          lead: "PADD, Nebraska Broadband Office (monitoring transition to permanent leadership), USDA RD, NTIA, local governments, ISPs",
          resources: "USDA ReConnect, NTIA BEAD (gap-fill round), state broadband programs, FCC E-Rate, forthcoming NTIA non-deployment fund guidance, EDA",
          timeline: "Years 1–3",
        },
        {
          strategy: "Years 2–5: Partner with ISPs and local governments on middle-mile infrastructure planning in underserved corridors. Identify at least 2 middle-mile opportunities and connect communities to feasibility support.",
          lead: "PADD, ISPs, municipalities, NRDs, Nebraska Broadband Office, NROC",
          resources: "NTIA BEAD middle-mile, USDA ReConnect, state programs, NROC resources",
          timeline: "Years 2–5",
        },
      ]),
      measures: [
        "# broadband serviceable locations in the Panhandle without access to 100/20 Mbps service via terrestrial reliable broadband: fiber, cable, DSL, or licensed/hybrid fixed wireless, excluding satellite and unlicensed-only fixed wireless (FCC Broadband Data Collection, annual)",
        "# Panhandle locations where satellite is the only service meeting 100/20 Mbps (FCC Broadband Data Collection, annual)",
        "Percent of Panhandle households with a broadband internet subscription (American Community Survey, Table B28002, annual)",
      ],
      notes: [
        { text: "Broadband buildout is this CEDS’s most direct near-term lever for behavioral-health access, since many behavioral-health services can be delivered remotely where specialist and surgical care cannot. The 100/20 Mbps threshold above is the same threshold required for reliable telehealth, and progress against it should be read alongside the region’s behavioral-health access gap documented in Chapter 6.3." },
        {
          source: true,
          text: "Source note. BEAD’s original definition of reliable broadband service excluded satellite and unlicensed-only fixed wireless; NTIA revised that definition in 2025 to be technology-neutral, meaning locations served only by satellite may now be classified as served. This CEDS reports terrestrial availability separately from satellite-only availability so that progress reflects infrastructure deployment rather than reclassification. FCC Broadband Data Collection measures provider-advertised availability, not measured throughput at the premises, and is known to overstate rural coverage. ACS Table B28002 measures household subscription and is reported alongside availability because availability alone does not indicate service is affordable or in use.",
        },
      ],
    },
  ],
};

const goal4 = {
  number: "Goal 4",
  section: "9.4",
  title: "Build Regional Capacity, Accountability & Long-Term Economic Resilience",
  intro: [
    <>
      Population decline, agricultural income volatility, natural-resource
      pressure, and fiscal strain are regional conditions. No single community,
      county, or organization holds the jurisdiction or the resources to address
      them alone. Coordination that aligns public and private partners, protects
      the natural systems the regional economy depends on, and builds
      diversification capacity is what makes the other three goals achievable
      and durable.
    </>,
    <>
      Residents were specific about how they want that work done. Eighty-six
      percent rate measuring real-world outcomes rather than activities as
      important or very important, 80% say PADD should work with other
      organizations rather than alone, and 77% want progress information easy to
      find on a public website. At the same time, 41% had not heard of PADD
      before taking the survey and 17% have worked with PADD directly. The
      mandate and the visibility gap arrive together. This goal answers both:
      progress against every measure in this plan will be reviewed annually with
      the CEDS Strategy Council and published for the communities PADD serves.
    </>,
  ],
  objectives: [
    {
      id: "4.1",
      label: "Objective 4.1 — Regional Coordination, Capacity & Accountability",
      target: (
        <>
          <strong>By 2030,</strong> establish and sustain a formal regional
          economic-development coordination structure that convenes PADD, county
          and municipal governments, chambers, educational institutions, major
          employers, and natural-resource managers on a regular and productive
          basis — with documented, measurable outcomes from each convening
          cycle.
        </>
      ),
      rows: withLabel("4.1 Regional Coordination, Capacity & Accountability", [
        {
          strategy: "Year 1: Formalize the CEDS Strategy Council as the standing regional coordination body. Adopt a governance charter, meeting schedule, and annual reporting framework. Target: first full annual CEDS review completed by December 2027.",
          lead: "PADD (lead); county and municipal governments, chambers, WNCC, CSC, Regional West, major employers, NRDs",
          resources: "Staff time, EDA planning grant support, PADD operational budget",
          timeline: "Year 1",
        },
        {
          strategy: "Years 1–3: Develop and launch a regional economic-development dashboard — a publicly accessible, annually updated scorecard tracking progress on all CEDS KPIs. Publish on PADD’s website and share with elected officials, funders, and the public at each annual review.",
          lead: "PADD (lead); Nebraska DED, EDA, county governments, partner organizations",
          resources: "Staff time, EDA planning funds, Nebraska DED partnership, PADD website",
          timeline: "Years 1–3",
        },
        {
          strategy: "Year 1, then ongoing: Launch a Panhandle Opportunity Tracker — a real-time, plain-language feed of open grant and funding opportunities relevant to Panhandle communities, pushed to members and partners as deadlines approach. Directly answers the ‘I never hear about funding in time’ barrier raised by Strategy Council applicants.",
          lead: "PADD (lead); Nebraska DED, EDA, USDA RD, member communities",
          resources: "Staff time, PADD website/portal, existing grant-alert feeds",
          timeline: "Year 1–5",
        },
        {
          strategy: "Years 2–5: Pursue and execute at least 3 multi-jurisdictional economic-development projects — initiatives crossing county lines requiring cooperation among at least 3 counties or municipalities — demonstrating the added value of regional coordination over individual community approaches.",
          lead: "PADD, county governments, municipalities, NRDs, regional employers, educational institutions",
          resources: "EDA Planning & Local Technical Assistance, USDA RD, DED, CDBG",
          timeline: "Years 2–5",
        },
        {
          strategy: "Ongoing, Years 1–5: Actively represent the Panhandle in state and federal policy processes affecting rural economic development (Legislature sessions, USDA rulemaking, EDA planning cycles, and Nebraska DED priority-setting.) Document and report on policy outcomes.",
          lead: "PADD (lead); elected officials, NROC, NACO, Nebraska League of Municipalities",
          resources: "Staff time, PADD operational budget, NROC resources",
          timeline: "Years 1–5",
        },
      ]),
      measures: [
        "# CEDS Strategy Council meetings held annually (target: minimum 2 per year)",
        "Annual CEDS review completed and published publicly",
        "# of the eleven counties with at least one documented CEDS activity each year (target: all 11 by Year 3) — ensures benefits are not concentrated in Scotts Bluff County",
        "# funding alerts distributed through the Panhandle Opportunity Tracker",
        "# of communities under 1,000 population with a documented CEDS activity, cumulative (target: at least 6 by year 5)",
        "# performance measures with a current-year value reported at annual review, of 31 (target: 27 by Year 3)",
      ],
    },
    {
      id: "4.2",
      label: "Objective 4.2 — Natural Resource and Economic Resilience",
      target: (
        <>
          <strong>By 2030,</strong> implement at least 3 regional initiatives
          that strengthen the long-term economic resilience of the Panhandle
          through natural-resource stewardship, economic diversification, and
          climate adaptation — reducing vulnerability to agricultural income
          volatility, aquifer depletion, and climate-driven disruptions.
        </>
      ),
      rows: withLabel("4.2 Natural Resource & Economic Resilience", [
        {
          strategy: "Year 1–2: Convene a Water Security Working Group with the North Platte NRD, Twin Platte NRD, and Upper Niobrara-White NRD to document aquifer trends, identify at-risk agricultural communities, and develop a water-security communication and planning resource. Publish findings by end of Year 2.",
          lead: "PADD (lead); North Platte NRD, Twin Platte NRD, Upper Niobrara-White NRD, UNL Extension, NE Dept. of Natural Resources",
          resources: "Staff time, NRD partnership, NE Natural Resources Commission, USDA NRCS, UNL Extension",
          timeline: "Years 1–2",
        },
        {
          strategy: "Years 1–3: Develop a Panhandle Rural Economy Diversification Strategy focused on the six target sectors from Chapter 5 — precision agriculture, renewable energy, healthcare/telehealth, remote-work attraction, tourism, and value-added agriculture. Assign a lead and Year 1 action for each. Present to the Council for adoption.",
          lead: "PADD (lead); Scotts Bluff County ED, Box Butte Development Corp., Panhandle Partnership, chambers, NRDs, educational institutions",
          resources: "EDA Planning & Local Technical Assistance, Nebraska DED, USDA RD, PADD operational budget",
          timeline: "Years 1–3",
        },
        {
          strategy: "Years 2–5: Support development of at least 2 renewable-energy feasibility studies for counties with strong wind or solar resources — engaging commissioners, landowners, NRDs, and utilities in a transparent community-benefit framework. Track from feasibility to project-proposal stage.",
          lead: "PADD, county governments, NPPD, PREMA, NDEE, Nebraska Power Review Board, landowner groups",
          resources: "NDEE renewable-energy TA, DOE RAISERS, IRA clean-energy tax credits, transmission planning programs",
          timeline: "Years 2–5",
        },
        {
          strategy: "Years 2–5: With the Nebraska Tourism Commission and Nebraska Travel Association, develop and launch a coordinated Panhandle regional tourism brand and visitor-infrastructure strategy — regional wayfinding, Passport Program activation in at least 5 new communities, and a regional hospitality capacity-building program.",
          lead: "PADD, Visit Scottsbluff-Gering, Dawes County Tourism, NE Tourism Commission, NETA, Fort Robinson SP, NPS",
          resources: "NE Tourism Commission grants, EDA, USDA RD Community Facilities, NE Game & Parks, private hospitality investment",
          timeline: "Years 2–5",
        },
      ]),
      measures: [
        "# aquifer trend reports published and distributed to regional stakeholders",
        "# renewable-energy feasibility studies completed in Panhandle counties",
        "# multi-jurisdictional tourism initiatives implemented",
      ],
    },
  ],
};

const GOALS = [goal1, goal2, goal3, goal4];

// 9.5 Implementation Schedule — one-time deliverables, tracked complete / not complete
const MILESTONES = [
  ["Year 1", "Regional housing asset inventory completed", "1.1"],
  ["Year 1", "Childcare baseline established — licensed slots vs. households with children under 12", "1.2"],
  ["Year 1", "Panhandle Business Succession Registry established", "2.1"],
  ["Year 1", "Regional employer needs survey completed (minimum 40 respondents)", "2.2"],
  ["Year 1", "Infrastructure needs inventory completed across all eleven counties, with projects tiered", "3.1"],
  ["Year 1", "Broadband gap assessment completed — serviceable locations below 100/20 Mbps identified by county", "3.2"],
  ["Year 1", "Public CEDS performance dashboard live", "4.1"],
  ["Year 1", "PADD infrastructure grant award history compiled and award-rate target range established", "3.1"],
  ["Year 2", "Panhandle Opportunity Tracker live", "4.1"],
  ["Year 2", "At least 3 employers convened on childcare models, outcomes documented and shared", "1.2"],
  ["Year 2", "Water Security Working Group convened", "4.2"],
  ["Year 3", "Panhandle Rural Economy Diversification Strategy completed with assigned leads", "4.2"],
  ["Year 3", "PADD recognition re-surveyed against the 41% “never heard of PADD” baseline", "4.1"],
  ["Year 4", "Regional employer needs survey repeated and compared against the Year 1 baseline", "2.2"],
  ["Year 5", "Regional tourism brand and wayfinding strategy launched", "4.2"],
  ["Year 5", "Final five-year CEDS performance report published; 2031–2036 planning process initiated", "4.1"],
];

// ─── Goal badge colors ────────────────────────────────────────────────────────
const GOAL_COLORS = ["#A84F30", "#213A3E", "#122326", "#5C7A4E"];

// ─── Component ───────────────────────────────────────────────────────────────

function GoalSection({ goal, color }) {
  return (
    <section className="ch9-goal">
      <div className="ch9-goal-header" style={{ borderLeftColor: color }}>
        <span className="ch9-goal-number" style={{ backgroundColor: color }}>
          {goal.section} {goal.number}
        </span>
        <h3 className="ch9-goal-title">{goal.title}</h3>
      </div>

      <div className="ch9-area-of-need">
        {goal.intro.map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>

      {goal.objectives.map((obj) => (
        <div key={obj.id} className="ch9-objective">
          <h4 className="ch9-objective-label">{obj.label}</h4>
          <p className="ch9-objective-target">{obj.target}</p>
          <ObjectiveTable rows={obj.rows} />
          <div className="ch9-measures">
            <strong>Performance Measures</strong>
            <ul>
              {obj.measures.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
          {obj.notes?.map((n, i) => (
            <p key={i} className={n.source ? "source-note" : "ch9-goal-note"}>{n.text}</p>
          ))}
        </div>
      ))}
    </section>
  );
}

export default function Chapter9Dashboard() {
  return (
    <div className="chapter chapter9">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 9</p>
        <h2>Regional Action Plan</h2>
        <p className="chapter-lede">
          Four goals, eight objectives, and specific, time-bound strategies, each
          with an assigned lead, funding resources, and performance measures
          built around outcomes, not activities.
        </p>
        <div className="ch9-intro">
          <p>
            The Regional Action Plan translates the evidence base of Chapters
            2–7 and the strategic framework of the SWOT into four concrete
            goals, eight objectives, and a set of specific, time-bound
            strategies with assigned lead organizations, funding resources, and
            performance measures. Each objective is structured around{" "}
            <strong>lagging outcomes</strong> (units built, businesses
            transitioned, connections established) rather than leading
            activities such as meetings held or applications submitted. This is
            the core improvement over previous CEDS cycles.
          </p>
          <p>
            Each goal below opens with the regional conditions it responds to
            and the evidence supporting it, drawn from Chapters 2 through 8 and
            the community input documented in Chapter 11.
          </p>
        </div>

        {GOALS.map((goal, i) => (
          <GoalSection key={goal.number} goal={goal} color={GOAL_COLORS[i]} />
        ))}

        <h3>9.5 Implementation Schedule</h3>
        <p>
          The performance measures listed under each goal are recurring,
          reported as numbers at each annual review. The commitments below are
          one-time deliverables spanning all four goals, tracked as complete or
          not complete against a target year. Both are reported together at
          each annual review and together form the basis of PADD’s CEDS Annual
          Performance Report.
        </p>
        <div className="table-wrapper">
          <table className="ceds-table">
            <thead>
              <tr>
                <th scope="col">Target Year</th>
                <th scope="col">Milestone</th>
                <th scope="col">Objective</th>
              </tr>
            </thead>
            <tbody>
              {MILESTONES.map(([year, milestone, obj]) => (
                <tr key={milestone}>
                  <td>{year}</td>
                  <td>{milestone}</td>
                  <td>{obj}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
